import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../supabase';
import AdminDashboardNav from '../comps/AdminDashboardNav';
import { generateReport } from '../../utils/generateReport';
import { FiFileText, FiRefreshCw, FiCalendar, FiDownload, FiShoppingCart, FiDollarSign, FiTrendingUp, FiLock, FiMail, FiBarChart2 } from 'react-icons/fi';
import '../styles/admin.css';

export default function ReportPage() {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportRange, setExportRange] = useState('month');
  const [exportCustomFrom, setExportCustomFrom] = useState('');
  const [exportCustomTo, setExportCustomTo] = useState('');
  const [emailRange, setEmailRange] = useState('month');
  const [emailCustomFrom, setEmailCustomFrom] = useState('');
  const [emailCustomTo, setEmailCustomTo] = useState('');
  const [generatingReport, setGeneratingReport] = useState(false);
  const [exportPdfPassword, setExportPdfPassword] = useState('');
  const [exportPasswordEnabled, setExportPasswordEnabled] = useState(false);
  const [emailPdfPassword, setEmailPdfPassword] = useState('');
  const [emailPasswordEnabled, setEmailPasswordEnabled] = useState(false);
  const [reportEmail, setReportEmail] = useState('');
  const [emailLocked, setEmailLocked] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // null | 'sending' | 'sent' | 'error'
  const [emailError, setEmailError] = useState('');
  const [autoSendEnabled, setAutoSendEnabled] = useState(false);
  const [autoSendDate, setAutoSendDate] = useState('');
  const [autoSendFrequency, setAutoSendFrequency] = useState('once');
  // Derived from each order's own Firestore fields (refundedItems/fullyRefunded,
  // written by the backend's /refund-payment route), not localStorage - this page
  // generates and emails real revenue reports, so a stale per-browser cache here
  // would mean the numbers in those reports are simply wrong.
  const refundedItems = useMemo(() => {
    const map = {};
    orders.forEach(o => { if (o.refundedItems) Object.assign(map, o.refundedItems); });
    return map;
  }, [orders]);
  const refundedOrders = useMemo(() => {
    const map = {};
    orders.forEach(o => { if (o.fullyRefunded) map[o.orderReference] = true; });
    return map;
  }, [orders]);
  const [statsRange, setStatsRange] = useState('month');
  const [statsCustomFrom, setStatsCustomFrom] = useState('');
  const [statsCustomTo, setStatsCustomTo] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [{ data: orderRows, error: ordersError }, { data: productRows, error: productsError }] = await Promise.all([
        supabase.from('orders')
          .select('*, users(name, email), order_items(*), tickets(*), klippekort(*)')
          .order('date_purchased', { ascending: false }),
        supabase.from('products').select('*'),
      ]);
      if (ordersError) throw ordersError;
      if (productsError) throw productsError;

      const allOrders = (orderRows || [])
        .filter(o => o.order_reference)
        .map(o => {
          // Deduplicate: ticket/klippekort rows supercede order_items duplicates for lek/klippekort
          const subcollectionItems = [
            ...(o.tickets || []).map(t => ({ name: t.name, category: t.category, price: t.price, quantity: t.quantity })),
            ...(o.klippekort || []).map(k => ({ name: k.name, category: 'klippekort', price: null, quantity: 1 })),
          ];

          // If subcollections have items, exclude lek/klippekort/månedskort/gavekort from
          // order_items to avoid double-counting - but keep golf and other untouched categories
          const subcollectionCategories = new Set(
            subcollectionItems.map(i => (i.category || '').toLowerCase().trim())
          );
          const baseItems = (subcollectionItems.length > 0
            ? (o.order_items || []).filter(i => {
                const cat = (i.category || '').toLowerCase().trim();
                return !subcollectionCategories.has(cat) && cat !== 'lek' && cat !== 'klippekort' && cat !== 'månedskort' && cat !== 'gavekort';
              })
            : (o.order_items || [])
          ).map(i => ({ name: i.name, category: i.category, price: i.price, quantity: i.quantity }));
          let allItems = [...baseItems, ...subcollectionItems];

          // If still no items, all other categories (lek/klippekort/månedskort/gavekort)
          // are always stored in subcollections - so an order with nothing must be tilbehør.
          if (allItems.length === 0 && (o.total_price || 0) > 0) {
            allItems.push({ name: 'Tilbehør', category: 'tilbehør', price: o.total_price, quantity: 1 });
          }

          return {
            id: o.id,
            userId: o.user_id,
            userName: o.users?.name || 'Unknown',
            userEmail: o.users?.email || '',
            orderReference: o.order_reference,
            totalPrice: o.total_price,
            captureStatus: o.capture_status,
            datePurchased: o.date_purchased,
            refundedItems: o.refunded_items,
            fullyRefunded: o.fully_refunded,
            items: allItems,
          };
        });
      setOrders(allOrders);
      setProducts(productRows || []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  // Load persisted report-email lock from app_settings
  useEffect(() => {
    const loadEmailSetting = async () => {
      try {
        const { data } = await supabase.from('app_settings').select('value').eq('key', 'reportEmail').maybeSingle();
        const d = data?.value;
        if (d) {
          if (d.email) setReportEmail(d.email);
          if (d.locked) setEmailLocked(true);
          if (d.autoSendDate) { setAutoSendDate(d.autoSendDate); }
          if (d.autoSendEnabled) { setAutoSendEnabled(true); }
          if (d.autoSendFrequency) { setAutoSendFrequency(d.autoSendFrequency); }
        }
      } catch (_) {}
    };
    loadEmailSetting();
  }, []);

  const handleGenerate = async () => {
    setGeneratingReport(true);
    try {
      generateReport(orders, refundedItems, refundedOrders, products, {
        range: exportRange,
        customFrom: exportCustomFrom,
        customTo: exportCustomTo,
        password: exportPasswordEnabled && exportPdfPassword.trim() ? exportPdfPassword.trim() : null,
      });
    } catch (err) {
      alert('Kunne ikke generere rapporten: ' + err.message);
    }
    setGeneratingReport(false);
  };

  const handleEmailLockToggle = async () => {
    const newLocked = !emailLocked;
    setEmailLocked(newLocked);
    setEmailStatus(null);
    try {
      await supabase.from('app_settings').upsert({
        key: 'reportEmail',
        value: {
          email: reportEmail.trim(),
          locked: newLocked,
          autoSendEnabled,
          autoSendDate: autoSendEnabled ? autoSendDate : null,
          autoSendFrequency,
        },
      });
    } catch (err) {
      console.error('Failed to save email lock setting:', err);
    }
  };

  const handleAutoSendSave = async (enabled, date, frequency) => {
    const freq = frequency !== undefined ? frequency : autoSendFrequency;
    try {
      await supabase.from('app_settings').upsert({
        key: 'reportEmail',
        value: {
          email: reportEmail.trim(),
          locked: emailLocked,
          autoSendEnabled: enabled,
          autoSendDate: enabled ? date : null,
          autoSendFrequency: freq,
        },
      });
    } catch (err) {
      console.error('Failed to save auto-send setting:', err);
    }
  };

  const handleSendEmail = async () => {
    if (!reportEmail.trim()) return;
    setEmailStatus('sending');
    setEmailError('');
    try {
      const result = generateReport(orders, refundedItems, refundedOrders, products, {
        range: emailRange,
        customFrom: emailCustomFrom,
        customTo: emailCustomTo,
        password: emailPasswordEnabled && emailPdfPassword.trim() ? emailPdfPassword.trim() : null,
        returnBase64: true,
      });
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${backendUrl}/api/email/send-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          recipientEmail: reportEmail.trim(),
          pdfBase64: result.base64,
          periodLabel: result.periodLabel,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setEmailStatus('sent');
        setTimeout(() => setEmailStatus(null), 4000);
      } else {
        setEmailStatus('error');
        setEmailError(data.error || 'Ukjent feil');
      }
    } catch (err) {
      setEmailStatus('error');
      setEmailError(err.message);
    }
  };

  // Quick stats
  const now = new Date();
  const statsFilterBounds = (() => {
    switch (statsRange) {
      case 'today': { const s = new Date(now); s.setHours(0,0,0,0); const e = new Date(now); e.setHours(23,59,59,999); return { from: s, to: e }; }
      case 'week': { const s = new Date(now); s.setDate(now.getDate()-6); s.setHours(0,0,0,0); const e = new Date(now); e.setHours(23,59,59,999); return { from: s, to: e }; }
      case 'month': { return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: new Date(now.getFullYear(), now.getMonth()+1, 0, 23,59,59,999) }; }
      case 'lastmonth': { return { from: new Date(now.getFullYear(), now.getMonth()-1, 1), to: new Date(now.getFullYear(), now.getMonth(), 0, 23,59,59,999) }; }
      case 'year': { return { from: new Date(now.getFullYear(), 0, 1), to: new Date(now.getFullYear(), 11, 31, 23,59,59,999) }; }
      case 'custom': { return statsCustomFrom && statsCustomTo ? { from: new Date(statsCustomFrom), to: new Date(new Date(statsCustomTo).setHours(23,59,59,999)) } : null; }
      default: return null;
    }
  })();
  const statsOrders = (() => {
    const active = orders.filter(o =>
      o.captureStatus === 'CAPTURED' || !!refundedOrders[o.orderReference]
    );
    if (!statsFilterBounds) return active;
    return active.filter(o => { const d = new Date(o.datePurchased || 0); return d >= statsFilterBounds.from && d <= statsFilterBounds.to; });
  })();

  const totalGross = statsOrders.reduce((s, o) => s + (o.totalPrice || 0), 0);
  // Category breakdown (mirrors generateReport logic)
  const productCategoryMap = {};
  products.forEach(p => {
    if (p.name) productCategoryMap[p.name.toLowerCase()] = p.category || 'Ukjent';
    if (p.id) productCategoryMap[p.id.toLowerCase()] = p.category || 'Ukjent';
  });
  const KEYWORD_CATEGORY_MAP = [
    { keywords: ['golf', 'minigolf', 'mini golf'], category: 'golf' },
    { keywords: ['lek', 'hopp', 'trampoline', 'ninja', 'klatring'], category: 'lek' },
    { keywords: ['klippekort', 'klippe', 'stempel'], category: 'klippekort' },
    { keywords: ['månedskort', 'måneds', 'månedlig', 'månedspas'], category: 'månedskort' },
    { keywords: ['gavekort', 'gave'], category: 'gavekort' },
  ];
  const getCat = item => {
    if (item.category && item.category.toLowerCase() !== 'ukjent') return item.category.toLowerCase().trim();
    if (!item.name) return 'Ukjent';
    const key = item.name.toLowerCase().trim();
    if (productCategoryMap[key] && productCategoryMap[key].toLowerCase() !== 'ukjent') return productCategoryMap[key].toLowerCase();
    for (const [pName, cat] of Object.entries(productCategoryMap)) {
      if (cat && cat.toLowerCase() !== 'ukjent' && (key.includes(pName) || pName.includes(key))) return cat.toLowerCase();
    }
    for (const { keywords, category } of KEYWORD_CATEGORY_MAP) {
      if (keywords.some(kw => key.includes(kw))) return category;
    }
    return item.name;
  };
  const catMap = {};
  statsOrders.forEach(order => {
    const isFullRefund = !!(refundedOrders && refundedOrders[order.orderReference]);
    const items = order.items || order.products || [];
    const target = items.length > 0 ? items : null;
    if (target) {
      target.forEach(item => {
        const cat = getCat(item);
        if (!catMap[cat]) catMap[cat] = { gross: 0, net: 0, refunds: 0 };
        const itemGross = (item.price || 0) * (item.quantity || 1);
        const itemRefunded = isFullRefund
          ? itemGross
          : ((refundedItems && refundedItems[`${order.orderReference}_${item.name}`]) || 0) * (item.price || 0);
        catMap[cat].gross += itemGross;
        catMap[cat].net += itemGross - itemRefunded;
        catMap[cat].refunds += itemRefunded;
      });
    } else {
      const cat = 'Generelt';
      if (!catMap[cat]) catMap[cat] = { gross: 0, net: 0, refunds: 0 };
      const gross = order.totalPrice || 0;
      const orderRefund = isFullRefund ? gross : 0;
      catMap[cat].gross += gross;
      catMap[cat].net += isFullRefund ? 0 : gross;
      catMap[cat].refunds += orderRefund;
    }
  });
  const catRows = Object.entries(catMap)
    .map(([cat, d]) => ({
      cat,
      gross: d.gross,
      net: d.net / 1.12,
      refunds: d.refunds,
      _rawGross: d.gross,
    }))
    .sort((a, b) => a.cat.localeCompare(b.cat, 'no'));
  const catTotalGross = catRows.reduce((s, r) => s + r._rawGross, 0);
  const catTotalRefunds = catRows.reduce((s, r) => s + r.refunds, 0);
  catRows.forEach(r => { r.pct = catTotalGross > 0 ? (r._rawGross / catTotalGross) * 100 : 0; });
  const totalRefunds = statsOrders.reduce((s, o) => {
    if (refundedOrders && refundedOrders[o.orderReference]) return s + (o.totalPrice || 0);
    const items = o.items || o.products || [];
    return s + items.reduce((is, item) => {
      const qty = (refundedItems && refundedItems[`${o.orderReference}_${item.name}`]) || 0;
      return is + qty * (item.price || 0);
    }, 0);
  }, 0);
  const netRevenue = totalGross - totalRefunds;

  return (
    <>
      <AdminDashboardNav />
      <div className="admin-dashboard">
        <div className="admin-header">
          <div>
            <h1><FiFileText style={{ marginRight: '12px', verticalAlign: 'middle' }} />Rapporter- og data</h1>
            <p>Generer detaljerte PDF-rapporter over inntekter og bestillinger</p>
          </div>
          <div className="admin-actions">
            <button className="refresh-btn" onClick={fetchData} disabled={loading}>
              <FiRefreshCw />
              {loading ? 'Laster...' : 'Oppdater data'}
            </button>
          </div>
        </div>

        {/* Stats + Category overview section */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

          {/* Filter bar */}
          {(() => {
            const periodOpts = [
              { value: 'today', label: 'I dag' },
              { value: 'week', label: 'Siste 7 dager' },
              { value: 'month', label: 'Denne måneden' },
              { value: 'lastmonth', label: 'Forrige måned' },
              { value: 'year', label: 'I år' },
              { value: 'all', label: 'Alle tider' },
              { value: 'custom', label: 'Egendefinert' },
            ];
            const customDates = statsRange === 'custom' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <input id="stats-custom-from" type="date" value={statsCustomFrom} onChange={e => setStatsCustomFrom(e.target.value)}
                    style={{ height: '34px', borderRadius: '8px', border: '1px solid #dde1ea', padding: '0 10px', fontSize: '0.82rem', color: 'transparent', minWidth: '120px' }} />
                  <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.82rem', color: '#1e293b', pointerEvents: 'none' }}>
                    {statsCustomFrom ? statsCustomFrom.split('-').reverse().join('.') : 'Fra'}
                  </span>
                </div>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <input id="stats-custom-to" type="date" value={statsCustomTo} onChange={e => setStatsCustomTo(e.target.value)}
                    style={{ height: '34px', borderRadius: '8px', border: '1px solid #dde1ea', padding: '0 10px', fontSize: '0.82rem', color: 'transparent', minWidth: '120px' }} />
                  <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.82rem', color: '#1e293b', pointerEvents: 'none' }}>
                    {statsCustomTo ? statsCustomTo.split('-').reverse().join('.') : 'Til'}
                  </span>
                </div>
              </div>
            );
            return (
              <div className="report-filter-bar">
                <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', letterSpacing: '0.04em', textTransform: 'uppercase', flexShrink: 0 }}>Oversikt:</span>

                {/* Tile buttons — visible on large screens */}
                <div className="report-filter-tiles">
                  {periodOpts.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setStatsRange(opt.value)}
                      style={{
                        padding: '0.3rem 0.85rem', borderRadius: '7px',
                        border: statsRange === opt.value ? '2px solid #010f3c' : '2px solid #dde1ea',
                        background: statsRange === opt.value ? '#010f3c' : '#fff',
                        color: statsRange === opt.value ? '#fff' : '#475569',
                        fontWeight: '500', fontSize: '0.8rem', cursor: 'pointer',
                        fontFamily: 'Poppins, sans-serif',
                        transition: 'background 0.15s, border-color 0.15s, color 0.15s',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                  {customDates}
                </div>


              </div>
            );
          })()}

        {/* Quick stats */}
        <div className="stats-grid stats-grid-5">
          <div className="stat-card secondary">
            <div className="stat-card-header">
              <div className="stat-icon-circle"><FiShoppingCart /></div>
              <span className="stat-label">Totale bestillinger</span>
            </div>
            <div className="stat-value">{loading ? <span className="skeleton skeleton-value" /> : statsOrders.length}</div>
          </div>
          <div className="stat-card primary">
            <div className="stat-card-header">
              <div className="stat-icon-circle"><FiTrendingUp /></div>
              <span className="stat-label">Brutto inkl. MVA</span>
            </div>
            <div className="stat-value">{loading ? <span className="skeleton skeleton-value" /> : `${totalGross.toFixed(0)} kr`}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-header">
              <div className="stat-icon-circle"><FiDollarSign /></div>
              <span className="stat-label">Netto omsetning</span>
            </div>
            <div className="stat-value">{loading ? <span className="skeleton skeleton-value" /> : `${netRevenue.toFixed(0)} kr`}</div>
            {!loading && totalRefunds > 0 && (
              <span className="stat-badge">brutto minus refusjoner</span>
            )}
          </div>
          <div className="stat-card">
            <div className="stat-card-header">
              <div className="stat-icon-circle"><FiTrendingUp /></div>
              <span className="stat-label">Refusjoner</span>
            </div>
            <div className="stat-value">{loading ? <span className="skeleton skeleton-value" /> : `${Math.round(totalRefunds).toLocaleString('no-NO')} kr`}</div>
          </div>
        </div>

        {/* Category breakdown card */}
        <div className="card" style={{ padding: '1.75rem 2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.5rem' }}>
            <FiBarChart2 style={{ fontSize: '1.2rem', color: '#010f3c' }} />
            <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#010f3c' }}>Inntekt per kategori</h2>
          </div>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[1,2,3].map(i => <span key={i} className="skeleton" style={{ height: '36px', borderRadius: '8px', display: 'block' }} />)}
            </div>
          ) : catRows.length === 0 ? (
            <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Ingen data</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e8ecf2' }}>
                    <th style={{ textAlign: 'left', padding: '8px 10px 10px', color: '#64748b', fontWeight: '600', fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Kategori</th>
                    <th style={{ textAlign: 'right', padding: '8px 10px 10px', color: '#64748b', fontWeight: '600', fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Brutto inkl. MVA</th>
                    <th style={{ textAlign: 'right', padding: '8px 10px 10px', color: '#64748b', fontWeight: '600', fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Refundert</th>
                    <th style={{ textAlign: 'right', padding: '8px 10px 10px', color: '#64748b', fontWeight: '600', fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Netto eksl. MVA</th>
                  </tr>
                </thead>
                <tbody>
                  {catRows.map((row, i) => (
                    <tr key={row.cat} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                      <td style={{ padding: '10px 10px', fontWeight: '600', color: '#1e293b' }}>{row.cat}</td>
                      <td style={{ padding: '10px 10px', textAlign: 'right', color: '#1e293b' }}>{Math.round(row.gross).toLocaleString('no-NO')} kr</td>
                      <td style={{ padding: '10px 10px', textAlign: 'right', color: '#64748b' }}>{row.refunds > 0 ? `-${Math.round(row.refunds).toLocaleString('no-NO')} kr` : '—'}</td>
                      <td style={{ padding: '10px 10px', textAlign: 'right', color: '#1e293b' }}>{Math.round(row.net).toLocaleString('no-NO')} kr</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid #e8ecf2', background: '#f0f4ff' }}>
                    <td style={{ padding: '10px 10px', fontWeight: '700', color: '#010f3c' }}>Totalt</td>
                    <td style={{ padding: '10px 10px', textAlign: 'right', fontWeight: '700', color: '#010f3c' }}>{Math.round(catTotalGross).toLocaleString('no-NO')} kr</td>
                    <td style={{ padding: '10px 10px', textAlign: 'right', fontWeight: '700', color: '#010f3c' }}>{catTotalRefunds > 0 ? `-${Math.round(catTotalRefunds).toLocaleString('no-NO')} kr` : '—'}</td>
                    <td style={{ padding: '10px 10px', textAlign: 'right', fontWeight: '700', color: '#010f3c' }}>{Math.round((catTotalGross - catTotalRefunds) / 1.12).toLocaleString('no-NO')} kr</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        </div>{/* end stats + category wrapper */}

        {/* Export settings card */}
        <div className="card" style={{ padding: '1.75rem 2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.5rem' }}>
            <FiDownload style={{ fontSize: '1.2rem', color: '#010f3c' }} />
            <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#010f3c' }}>Eksportinnstillinger</h2>
          </div>

          {/* Period selector */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', letterSpacing: '0.04em', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>Rapportperiode</label>
            <select
              value={exportRange}
              onChange={e => setExportRange(e.target.value)}
              style={{
                height: '40px', borderRadius: '8px', border: '1px solid #dde1ea',
                padding: '0 12px', fontSize: '0.9rem', color: '#1e293b',
                background: '#fff', cursor: 'pointer', width: '100%', maxWidth: '320px',
              }}
            >
              <option value="today">I dag</option>
              <option value="week">Siste 7 dager</option>
              <option value="month">Denne måneden</option>
              <option value="lastmonth">Forrige måned</option>
              <option value="year">I år</option>
              <option value="all">Alle tider</option>
              <option value="custom">Egendefinert</option>
            </select>
            {exportRange === 'custom' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '0.85rem', color: '#555' }}>Fra:</label>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <input type="date" value={exportCustomFrom} onChange={e => setExportCustomFrom(e.target.value)}
                    style={{ height: '36px', borderRadius: '8px', border: '1px solid #dde1ea', padding: '0 10px', fontSize: '0.85rem', color: 'transparent', minWidth: '130px' }} />
                  <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.85rem', color: '#1e293b', pointerEvents: 'none' }}>
                    {exportCustomFrom ? exportCustomFrom.split('-').reverse().join('.') : ''}
                  </span>
                </div>
                <label style={{ fontSize: '0.85rem', color: '#555' }}>Til:</label>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <input type="date" value={exportCustomTo} onChange={e => setExportCustomTo(e.target.value)}
                    style={{ height: '36px', borderRadius: '8px', border: '1px solid #dde1ea', padding: '0 10px', fontSize: '0.85rem', color: 'transparent', minWidth: '130px' }} />
                  <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.85rem', color: '#1e293b', pointerEvents: 'none' }}>
                    {exportCustomTo ? exportCustomTo.split('-').reverse().join('.') : ''}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* PDF-passord */}
          <div id="rapport-pdf-passord" style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '1.5rem' }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer',
              width: 'fit-content', userSelect: 'none',
            }}>
              <div
                onClick={() => setExportPasswordEnabled(v => !v)}
                style={{
                  width: '36px', height: '20px', borderRadius: '10px', flexShrink: 0,
                  background: exportPasswordEnabled ? '#010f3c' : '#dde1ea',
                  position: 'relative', transition: 'background 0.2s', cursor: 'pointer',
                }}
              >
                <div style={{
                  width: '14px', height: '14px', borderRadius: '50%', background: '#fff',
                  position: 'absolute', top: '3px',
                  left: exportPasswordEnabled ? '19px' : '3px',
                  transition: 'left 0.18s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                }} />
              </div>
              <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#334155' }}>
                Passordbeskytt PDF
              </span>
            </label>
            {exportPasswordEnabled && (
              <div style={{ position: 'relative', maxWidth: '280px' }}>
                <FiLock style={{
                  position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)',
                  fontSize: '0.82rem', color: exportPdfPassword ? '#010f3c' : '#94a3b8', pointerEvents: 'none',
                }} />
                <input
                  id="rapport-pdf-passord-input"
                  type="password"
                  value={exportPdfPassword}
                  onChange={e => setExportPdfPassword(e.target.value)}
                  placeholder="Skriv inn passord"
                  autoFocus
                  style={{
                    height: '40px', borderRadius: '8px',
                    border: `1.5px solid ${exportPdfPassword ? '#010f3c' : '#dde1ea'}`,
                    paddingLeft: '34px', paddingRight: '12px', fontSize: '0.9rem', background: '#fff',
                    outline: 'none', width: '100%', boxSizing: 'border-box', color: '#1e293b',
                    transition: 'border-color 0.2s',
                  }}
                />
              </div>
            )}
          </div>

          {/* Export PDF button */}
          <button
            onClick={handleGenerate}
            disabled={generatingReport || loading}
            style={{
              height: '40px', padding: '0 24px', borderRadius: '8px',
              border: 'none',
              background: (generatingReport || loading) ? '#94a3b8' : '#010f3c',
              color: '#fff',
              fontSize: '0.875rem', fontWeight: '600',
              cursor: (generatingReport || loading) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: '7px', whiteSpace: 'nowrap',
              fontFamily: 'Poppins, sans-serif',
              transition: 'background 0.2s',
            }}
          >
            <FiDownload style={{ fontSize: '0.9rem' }} />
            {generatingReport ? 'Genererer…' : 'Last ned PDF-rapport'}
          </button>

        </div>

        {/* Send til e-post card */}
        <div className="card" style={{ padding: '1.75rem 2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '0.4rem' }}>
            <FiMail style={{ fontSize: '1.2rem', color: '#010f3c' }} />
            <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#010f3c' }}>Send til e-post</h2>
          </div>
          <p style={{ fontSize: '0.83rem', color: '#94a3b8', margin: '0 0 1.5rem 0' }}>
            Generer og send rapporten som PDF-vedlegg. Låst adresse deles på tvers av alle admin-brukere.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px', maxWidth: '560px' }}>

            {/* Period dropdown */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Rapportperiode</label>
              <div style={{ position: 'relative' }}>
                <FiCalendar style={{
                  position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)',
                  fontSize: '0.85rem', color: '#94a3b8', pointerEvents: 'none',
                }} />
                <select
                  value={emailRange}
                  onChange={e => setEmailRange(e.target.value)}
                  style={{
                    height: '40px', borderRadius: '8px', border: '1.5px solid #dde1ea',
                    paddingLeft: '34px', paddingRight: '32px', fontSize: '0.9rem',
                    background: '#fff', outline: 'none', width: '100%',
                    boxSizing: 'border-box', color: '#1e293b', cursor: 'pointer',
                    appearance: 'none', WebkitAppearance: 'none',
                  }}
                >
                  <option value="today">I dag</option>
                  <option value="week">Siste 7 dager</option>
                  <option value="month">Denne måneden</option>
                  <option value="lastmonth">Forrige måned</option>
                  <option value="year">I år</option>
                  <option value="all">Alle tider</option>
                  <option value="custom">Egendefinert</option>
                </select>
                <svg style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#94a3b8' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
              </div>
              {emailRange === 'custom' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '6px', flexWrap: 'wrap' }}>
                  <label style={{ fontSize: '0.85rem', color: '#555' }}>Fra:</label>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <input type="date" value={emailCustomFrom} onChange={e => setEmailCustomFrom(e.target.value)}
                      style={{ height: '36px', borderRadius: '8px', border: '1px solid #dde1ea', padding: '0 10px', fontSize: '0.85rem', color: 'transparent', minWidth: '130px' }} />
                    <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.85rem', color: '#1e293b', pointerEvents: 'none' }}>
                      {emailCustomFrom ? emailCustomFrom.split('-').reverse().join('.') : ''}
                    </span>
                  </div>
                  <label style={{ fontSize: '0.85rem', color: '#555' }}>Til:</label>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <input type="date" value={emailCustomTo} onChange={e => setEmailCustomTo(e.target.value)}
                      style={{ height: '36px', borderRadius: '8px', border: '1px solid #dde1ea', padding: '0 10px', fontSize: '0.85rem', color: 'transparent', minWidth: '130px' }} />
                    <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.85rem', color: '#1e293b', pointerEvents: 'none' }}>
                      {emailCustomTo ? emailCustomTo.split('-').reverse().join('.') : ''}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Email field */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', letterSpacing: '0.04em', textTransform: 'uppercase' }}>E-postadresse</label>
              <div style={{ position: 'relative' }}>
                <FiMail style={{
                  position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)',
                  fontSize: '0.85rem', color: emailLocked ? '#010f3c' : '#94a3b8', pointerEvents: 'none', zIndex: 2,
                }} />
                {emailLocked && (
                  <div style={{
                    position: 'absolute', left: '34px', right: '96px', top: 0, bottom: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    pointerEvents: 'none', zIndex: 2, gap: '6px',
                  }}>
                    <div style={{
                      flex: 1, height: '1.5px',
                      background: 'linear-gradient(to right, transparent, #010f3c55)',
                    }} />
                    <FiLock style={{ fontSize: '0.75rem', color: '#010f3c', opacity: 0.45, flexShrink: 0 }} />
                    <div style={{
                      flex: 1, height: '1.5px',
                      background: 'linear-gradient(to left, transparent, #010f3c55)',
                    }} />
                  </div>
                )}
                <input
                  id="rapport-epost-input"
                  type="email"
                  value={reportEmail}
                  onChange={e => { setReportEmail(e.target.value); setEmailStatus(null); }}
                  placeholder="navn@eksempel.no"
                  disabled={emailLocked}
                  style={{
                    height: '40px', borderRadius: '8px',
                    border: '1.5px solid #dde1ea',
                    paddingLeft: '34px', paddingRight: '96px', fontSize: '0.9rem',
                    background: emailLocked ? '#f0f4ff' : '#fff',
                    outline: 'none', width: '100%', boxSizing: 'border-box', color: '#1e293b',
                    cursor: emailLocked ? 'not-allowed' : 'text',
                    transition: 'background 0.2s',
                  }}
                />
                <button
                  id="rapport-epost-lås"
                  onClick={handleEmailLockToggle}
                  disabled={!reportEmail.trim()}
                  title={emailLocked ? 'Lås opp e-post' : 'Lås e-post'}
                  style={{
                    position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)',
                    height: '32px', padding: '0 12px', borderRadius: '6px',
                    border: 'none',
                    background: emailLocked ? '#010f3c' : '#e8ecf5',
                    color: emailLocked ? '#fff' : '#475569',
                    cursor: reportEmail.trim() ? 'pointer' : 'not-allowed',
                    opacity: reportEmail.trim() ? 1 : 0.4,
                    display: 'flex', alignItems: 'center', gap: '5px',
                    fontSize: '0.8rem', fontWeight: '600', whiteSpace: 'nowrap',
                    fontFamily: 'Poppins, sans-serif',
                    transition: 'background 0.2s, color 0.2s',
                  }}
                >
                  <FiLock style={{ fontSize: '0.78rem' }} />
                  {emailLocked ? 'Lås opp' : 'Lås'}
                </button>
              </div>
            </div>

            {/* PDF password toggle */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{
                display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer',
                width: 'fit-content', userSelect: 'none',
              }}>
                <div
                  onClick={() => setEmailPasswordEnabled(v => !v)}
                  style={{
                    width: '36px', height: '20px', borderRadius: '10px', flexShrink: 0,
                    background: emailPasswordEnabled ? '#010f3c' : '#dde1ea',
                    position: 'relative', transition: 'background 0.2s', cursor: 'pointer',
                  }}
                >
                  <div style={{
                    width: '14px', height: '14px', borderRadius: '50%', background: '#fff',
                    position: 'absolute', top: '3px',
                    left: emailPasswordEnabled ? '19px' : '3px',
                    transition: 'left 0.18s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                  }} />
                </div>
                <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#334155' }}>
                  Passordbeskytt PDF
                </span>
              </label>
              {emailPasswordEnabled && (
                <div style={{ position: 'relative', maxWidth: '280px' }}>
                  <FiLock style={{
                    position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)',
                    fontSize: '0.82rem', color: emailPdfPassword ? '#010f3c' : '#94a3b8', pointerEvents: 'none',
                  }} />
                  <input
                    type="password"
                    value={emailPdfPassword}
                    onChange={e => setEmailPdfPassword(e.target.value)}
                    placeholder="Skriv inn passord"
                    autoFocus
                    style={{
                      height: '40px', borderRadius: '8px',
                      border: `1.5px solid ${emailPdfPassword ? '#010f3c' : '#dde1ea'}`,
                      paddingLeft: '34px', paddingRight: '12px', fontSize: '0.9rem', background: '#fff',
                      outline: 'none', width: '100%', boxSizing: 'border-box', color: '#1e293b',
                      transition: 'border-color 0.2s',
                    }}
                  />
                </div>
              )}
            </div>

            {/* Auto-send toggle */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{
                display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer',
                width: 'fit-content', userSelect: 'none',
              }}>
                <div
                  onClick={() => { const v = !autoSendEnabled; setAutoSendEnabled(v); handleAutoSendSave(v, autoSendDate, autoSendFrequency); }}
                  style={{
                    width: '36px', height: '20px', borderRadius: '10px', flexShrink: 0,
                    background: autoSendEnabled ? '#010f3c' : '#dde1ea',
                    position: 'relative', transition: 'background 0.2s', cursor: 'pointer',
                  }}
                >
                  <div style={{
                    width: '14px', height: '14px', borderRadius: '50%', background: '#fff',
                    position: 'absolute', top: '3px',
                    left: autoSendEnabled ? '19px' : '3px',
                    transition: 'left 0.18s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                  }} />
                </div>
                <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#334155' }}>
                  Auto send på dato
                </span>
              </label>
              {autoSendEnabled && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {/* Frequency selector */}
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {[
                      { value: 'once', label: 'Én gang' },
                      { value: 'daily', label: 'Daglig' },
                      { value: 'weekly', label: 'Ukentlig' },
                      { value: 'monthly', label: 'Månedlig' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => { setAutoSendFrequency(opt.value); handleAutoSendSave(autoSendEnabled, autoSendDate, opt.value); }}
                        style={{
                          padding: '0.3rem 0.85rem', borderRadius: '7px',
                          border: autoSendFrequency === opt.value ? '2px solid #010f3c' : '2px solid #dde1ea',
                          background: autoSendFrequency === opt.value ? '#010f3c' : '#fff',
                          color: autoSendFrequency === opt.value ? '#fff' : '#475569',
                          fontWeight: '500', fontSize: '0.8rem', cursor: 'pointer',
                          fontFamily: 'Poppins, sans-serif',
                          transition: 'background 0.15s, border-color 0.15s, color 0.15s',
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {/* Date picker */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <label style={{ fontSize: '0.8rem', color: '#64748b' }}>
                      {autoSendFrequency === 'once' ? 'Send dato:' : 'Startdato:'}
                    </label>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <input
                        type="date"
                        value={autoSendDate}
                        onChange={e => { setAutoSendDate(e.target.value); handleAutoSendSave(autoSendEnabled, e.target.value, autoSendFrequency); }}
                        min={new Date().toISOString().split('T')[0]}
                        style={{
                          height: '36px', borderRadius: '8px',
                          border: `1.5px solid ${autoSendDate ? '#010f3c' : '#dde1ea'}`,
                          padding: '0 12px', fontSize: '0.85rem', background: '#fff',
                          outline: 'none', color: 'transparent', boxSizing: 'border-box',
                          minWidth: '140px', transition: 'border-color 0.2s',
                        }}
                      />
                      <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.85rem', color: autoSendDate ? '#1e293b' : '#94a3b8', pointerEvents: 'none' }}>
                        {autoSendDate ? autoSendDate.split('-').reverse().join('.') : 'dd.mm.åååå'}
                      </span>
                    </div>
                    {autoSendDate && (
                      <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                        {autoSendFrequency === 'once' && `Sendes ${new Date(autoSendDate).toLocaleDateString('nb-NO', { day: 'numeric', month: 'long', year: 'numeric' })}`}
                        {autoSendFrequency === 'daily' && `Daglig fra ${new Date(autoSendDate).toLocaleDateString('nb-NO', { day: 'numeric', month: 'long' })}`}
                        {autoSendFrequency === 'weekly' && `Ukentlig fra ${new Date(autoSendDate).toLocaleDateString('nb-NO', { day: 'numeric', month: 'long' })}`}
                        {autoSendFrequency === 'monthly' && `Månedlig fra ${new Date(autoSendDate).toLocaleDateString('nb-NO', { day: 'numeric', month: 'long' })}`}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Divider */}
            <div style={{ borderTop: '1px solid #e8ecf2' }} />

            {/* Actions row — lock moved into input, just send + status here */}
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                id="rapport-epost-send"
                onClick={handleSendEmail}
                disabled={!emailLocked || emailStatus === 'sending' || loading}
                style={{
                  height: '40px', padding: '0 24px', borderRadius: '8px',
                  border: 'none',
                  background: (!emailLocked || emailStatus === 'sending' || loading) ? '#94a3b8' : '#010f3c',
                  color: '#fff',
                  fontSize: '0.875rem', fontWeight: '600',
                  cursor: (!emailLocked || emailStatus === 'sending' || loading) ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: '7px', whiteSpace: 'nowrap',
                  transition: 'background 0.2s',
                }}
              >
                <FiMail style={{ fontSize: '0.85rem' }} />
                {emailStatus === 'sending' ? 'Sender…' : 'Send nå'}
              </button>

              {emailLocked && emailStatus === null && (
                <span style={{ fontSize: '0.8rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#010f3c', flexShrink: 0 }} />
                  Låst til {reportEmail}
                </span>
              )}
              {emailStatus === 'sent' && (
                <span style={{ fontSize: '0.8rem', color: '#22c55e', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
                  Sendt!
                </span>
              )}
              {emailStatus === 'error' && (
                <span style={{ fontSize: '0.8rem', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ef4444', flexShrink: 0 }} />
                  {emailError}
                </span>
              )}
            </div>

          </div>
        </div>
      </div>
    </>
  );
}
